import {PartPublishingStatus} from "@buildwithflux/core";
import {IPartVersionData} from "@buildwithflux/models";
import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "injection/hooks";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * State for managing part version data and publishing status
 *
 * This replaces the Redux partVersion reducer state, which tracked the current
 * part version data and its publishing status.
 */
type PartVersionStoreState = {
    /**
     * The current part version data, if any
     */
    data: IPartVersionData | undefined;

    /**
     * The current publishing status of the part
     */
    publishStatus: PartPublishingStatus;

    /**
     * Sets the part version data
     */
    setPartVersionData: (partVersionData: IPartVersionData) => void;

    /**
     * Clears the part version data
     */
    clearPartVersionData: () => void;

    /**
     * Sets the publishing status
     */
    setPartPublishingStatus: (status: PartPublishingStatus) => void;
};

/**
 * The exported type of the store
 */
export type UsePartVersionStore = UseBoundStore<StoreApi<PartVersionStoreState>>;

/**
 * The exported type of the raw vanilla store (for non-React access like thunks)
 */
export type PartVersionStore = StoreApi<PartVersionStoreState>;

/**
 * Creates the vanilla non-bound-to-React part version store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 *
 * The store returned from this function cannot be directly used as a hook in a component. For that, see
 * `createPartVersionStoreAndHook` below.
 */
export function createPartVersionStore(): UsePartVersionStore {
    return create<PartVersionStoreState>()(
        devtools(
            (set): PartVersionStoreState => {
                return {
                    data: undefined,
                    publishStatus: PartPublishingStatus.idle,

                    setPartVersionData: (partVersionData: IPartVersionData) => {
                        if (!partVersionData) {
                            FluxLogger.captureError(new Error("setPartVersionData called without partVersionData"));
                            return;
                        }
                        set({data: partVersionData}, false, "setPartVersionData");
                    },

                    clearPartVersionData: () => {
                        set({data: undefined}, false, "clearPartVersionData");
                    },

                    setPartPublishingStatus: (status: PartPublishingStatus) => {
                        set({publishStatus: status}, false, "setPartPublishingStatus");
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "PartVersionStore"},
        ),
    );
}

/**
 * Creates both the raw store and the bound hook from the same store instance
 *
 * This is used by the service container to expose both the raw store (for thunks) and the hook (for React components)
 */
export function createPartVersionStoreAndHook(): {store: PartVersionStore; hook: UsePartVersionStore} {
    const store = createPartVersionStore();
    return {store, hook: store};
}

/**
 * Gets a reactive slice of the part version store: the part version data
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export function usePartVersionData(): IPartVersionData | undefined {
    return useFluxServices().usePartVersionStore(useShallow((state) => state.data));
}

/**
 * Gets a reactive slice of the part version store: the last published at timestamp (created_at)
 */
export function usePartVersionLastPublishedAt(): number | undefined {
    return useFluxServices().usePartVersionStore((state) => state.data?.created_at);
}

/**
 * Gets a reactive slice of the part version store: the preview image
 */
export function usePartVersionPreviewImage(): string | undefined {
    return useFluxServices().usePartVersionStore((state) => state.data?.preview_image);
}

/**
 * Gets a reactive slice of the part version store: the assets
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export function usePartVersionAssets(): IPartVersionData["assets"] | undefined {
    return useFluxServices().usePartVersionStore(useShallow((state) => state.data?.assets));
}

/**
 * Gets a reactive slice of the part version store: the controls
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export function usePartVersionControls(): IPartVersionData["controls"] | undefined {
    return useFluxServices().usePartVersionStore(useShallow((state) => state.data?.controls));
}

/**
 * Gets a reactive slice of the part version store: the publishing status
 */
export function usePartPublishingStatus(): PartPublishingStatus {
    return useFluxServices().usePartVersionStore((state) => state.publishStatus);
}

/**
 * Returns the function to set the part version data
 */
export function useSetPartVersionData(): (partVersionData: IPartVersionData) => void {
    return useFluxServices().usePartVersionStore((state) => state.setPartVersionData);
}

/**
 * Returns the function to clear the part version data
 */
export function useClearPartVersionData(): () => void {
    return useFluxServices().usePartVersionStore((state) => state.clearPartVersionData);
}

/**
 * Returns the function to set the publishing status
 */
export function useSetPartPublishingStatus(): (status: PartPublishingStatus) => void {
    return useFluxServices().usePartVersionStore((state) => state.setPartPublishingStatus);
}
